import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Avatar } from '~/components/Avatar';
import { Input } from '~/components/Input';
import { cn } from '~/lib/cn';
import { FlexCol } from '~/modules/artist-roster/components/flex/FlexCol';
import { FlexRow } from '~/modules/artist-roster/components/flex/FlexRow';
import type { PlanWizardForm } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';
import type { ArtistDetails } from '~/modules/artist-roster/types';

import { PlanSection } from '../PlanSection/PlanSection';

export interface PlanDetailsSectionProps {
  artistData: ArtistDetails;
}

export function PlanDetailsSection({ artistData }: PlanDetailsSectionProps) {
  const { t } = useTranslation('campaign-planner', {
    keyPrefix: 'digitalCampaigns.planWizard.sections.planDetails',
  });

  const { name, image: iconUrl } = artistData;
  const { register } = useFormContext<PlanWizardForm>();
  const titleClassName = cn('text-body-sm leading-tight text-strong');
  const avatarClassName = cn(
    'size-[28px] outline outline-2 bg-emphasis-subtle outline-emphasis-subtle',
  );

  return (
    <PlanSection title={t('title')} isRequired={false} isOpen={true}>
      <FlexRow className={cn('w-full gap-lg')}>
        <FlexCol className="gap-sm">
          <span className={titleClassName}>{t('artist')}</span>

          <FlexRow className="w-full flex-grow items-center gap-sm">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={name}
                className={cn('rounded-full object-cover', avatarClassName)}
                loading="lazy"
              />
            ) : (
              <Avatar name={name} variant="base" className={avatarClassName} />
            )}
            <span className="text-body-sm font-bold leading-none text-strong">
              {name}
            </span>
          </FlexRow>
        </FlexCol>

        <FlexCol className="gap-sm">
          <label htmlFor="campaign-name">
            <FlexRow className={cn(titleClassName, 'gap-2xs')}>
              <span>{t('campaignName')}</span>
              <span>*</span>
            </FlexRow>
          </label>
          <Input
            {...register('campaignName')}
            id="campaign-name"
            type="text"
            placeholder={t('namePlaceholder')}
            aria-label={t('campaignName')}
            className="w-[340px] leading-tight"
          />
        </FlexCol>

        {/* 
            @TODO WMG1-812 - Replace this with the track search control.
            Discussion here: https://wmggt.atlassian.net/browse/WMG1-812?focusedCommentId=814151
            */}
        {/* <FlexCol className="gap-sm">
          <span className={titleClassName}>{t('track')}</span>
          <div
            className={cn(
              'h-[48px] w-[340px] rounded-sm py-[12px] pl-md',
              'text-body-sm leading-tight placeholder:text-subtle',
              'outline outline-1 bg-emphasis-default outline-emphasis-strong',
            )}
          />
        </FlexCol> */}
      </FlexRow>
    </PlanSection>
  );
}

PlanDetailsSection.displayName = 'PlanDetailsSection';
